'use client';
import { useEffect, useId, useMemo, useCallback, useState } from 'react';
import {
  AdaptiveFlexLeftPane,
  AdaptiveFlexLeftPaneItem,
  AdaptiveFlexLeftPaneSearchAndButton,
  AdaptiveFlexPanel,
  AdaptiveFlexRightPane,
  Button,
  ConversationSettings,
  EmptyScreen,
  Icon,
  IConversationSet,
  useConversationContext,
  useMessageContext,
  Text,
  IConversation,
} from '@ui/components';
import { formatConversationPreviewTime } from '@ui/constants/date';
import { LeaderboardItemLoader } from '@ui/components/organisms/leaderboard/components/leaderboard-item';
import { CreateConversationModal } from './create-conversation-modal';
import { ConversationMessagesPane } from './conversation-messages-pane';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { getConversationPreviewText } from '@ui/components/organisms/message/message.utils';

export const ConversationAdaptivePanel = () => {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();
  const conv_id = search.get('conversation_id');
  const isOpenModal = search.get('openCreateModal');
  const id = useId();
  const state = useConversationContext();
  const messageState = useMessageContext('creator');
  const [storedConversation, setStoredConversation] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedConversation = sessionStorage.getItem('selectedConversation');
      if (savedConversation) {
        try {
          setStoredConversation(JSON.parse(savedConversation));
        } catch (error) {
          console.error('Failed to parse stored conversation:', error);
          sessionStorage.removeItem('selectedConversation');
        }
      }
    }
  }, []);

  const emptyListMessage = useMemo(() => {
    if (Boolean(state.searchValue)) {
      return {
        title: `No results for "${state.searchValue}"`,
        message: 'Try using different keywords.',
      };
    }

    if (!state.conversationSets.length && !Boolean(state.searchValue)) {
      return {
        title: "You haven't created any messages yet",
        message: 'Start by creating your first message.',
      };
    }

    return {
      title: '',
      message: '',
    };
  }, [state.selectedConversationSet, state.conversationSets, state.searchValue]);

  const onSelectConversation = useCallback(
    (conversation: IConversationSet) => {
      state.onSelectConversation(conversation);
      messageState.onSetCreator(conversation.conversation.creator_id);
      messageState.onSetThread(conversation.conversation.main_thread);
      messageState.onSetConversation(conversation.conversation.id);
      messageState.onSetFan(conversation.conversation.creator_id);
      messageState.onSetConversationMiscellaneous(conversation.conversation?.miscellaneous);
      messageState.onSetPage(1);
      !isOpenModal &&
        router.push(`/dashboard/messages?conversation_id=${conversation.conversation.id}`);
    },
    [state, messageState],
  );

  const onReadNewMessage = useCallback(
    (conversation: IConversationSet) => {
      // Mark conversation as read for creator app
      if (
        !conversation?.conversation?.opened_by_creator &&
        (conversation?.unread_messages_count ?? 0) > 0
      ) {
        state.markCreatorConversation({
          conversation_id: conversation?.conversation.id,
          creator_id: conversation?.conversation.creator_id,
        });
      }

      // Optional creator-side bulk read of message notifications when read
      if (state?.markConversationNotificationsAsRead) {
        state.markConversationNotificationsAsRead({
          user_id: state.creator_id,
          creator_id: conversation?.conversation.creator_id,
          conversation_id: conversation?.conversation.id,
        });
      }
    },
    [state],
  );

  useEffect(() => {
    if (state.isSingleConversationLoading) return;
    if (state.singleConversationSet) {
      onSelectConversation(state.singleConversationSet);
      return;
    }
  }, [state.singleConversationSet, state.isSingleConversationLoading]);

  useEffect(() => {
    if (state.isLoadingConversations) return;
    if (!state.selectedConversationSet && state.conversationSets.length > 0) {
      const firstConversation = state.conversationSets[0];
      onSelectConversation(firstConversation);
      return;
    }
  }, [state.conversationSets, state.isLoadingConversations]);

  useEffect(() => {
    if (!storedConversation) return;

    onSelectConversation(storedConversation);
    storedConversation && sessionStorage.removeItem('selectedConversation');

    return;
  }, [storedConversation]);

  const getSourceInfo = (conversation: IConversation) => {
    if (!conversation?.miscellaneous) return null;

    const source = conversation?.miscellaneous?.from;

    if (source === 'cohort') {
      return {
        type: 'Cohort',
        created_at: conversation?.miscellaneous?.created_at,
        name: conversation?.miscellaneous?.cohort_name,
        icon: 'people',
      };
    } else if (source === 'custom_leaderboard') {
      return {
        type: 'Leaderboard',
        created_at: conversation?.miscellaneous?.created_at,
        name: conversation?.miscellaneous?.custom_leaderboard_name,
        icon: 'trophy',
      };
    } else if (source === 'emails') {
      return {
        type: 'Emails',
        icon: 'openMessage',
      };
    }

    return null;
  };

  const getConversationTitle = useCallback((conversation: IConversation) => {
    if (conversation?.miscellaneous?.from !== 'direct_message') {
      return conversation?.name;
    }

    return (
      conversation?.miscellaneous?.direct_message_details?.created_by_name ||
      (conversation?.name && conversation?.name?.split(' ')[0])
    );
  }, []);

  return (
    <>
      <AdaptiveFlexPanel
        key={conv_id}
        selectedItemId={state.selectedConversationSet?.conversation.id}
        className="h-full max-h-[80vh] overflow-hidden">
        <AdaptiveFlexLeftPane className="relative flex h-full w-full flex-col space-y-2 overflow-hidden overflow-x-hidden px-0 pt-2 md:w-1/4">
          <div className="sticky top-0 z-10 bg-white px-2">
            <AdaptiveFlexLeftPaneSearchAndButton
              onSearch={state.handleSearch}
              onAddButtonClick={state.onAddConversation}
              placeholder="Search messages..."
              customButton={
                <CreateConversationModal {...state}>
                  <Button className="min-w-fit rounded" type="primary" size="smallIcon">
                    <Icon type="plus" />
                  </Button>
                </CreateConversationModal>
              }
            />
          </div>

          {state.isLoadingConversations && (
            <div className="flex flex-col gap-4 p-2">
              <LeaderboardItemLoader />
              <LeaderboardItemLoader />
              <LeaderboardItemLoader />
            </div>
          )}

          {!state.isLoadingConversations && !state.conversationSets.length && state.isSuccess && (
            <EmptyScreen
              title={emptyListMessage.title}
              message={emptyListMessage.message}
              button={<CreateConversationModal>Create message</CreateConversationModal>}
              onClick={state.onAddConversation}
              className="flex h-full min-h-full items-baseline justify-center rounded-none px-2 py-10 lg:py-10"
            />
          )}

          <div id={id} className="relative mt-4 h-full max-h-[calc(68vh)] overflow-y-auto">
            <InfiniteScroll
              className="h-full space-y-2 px-2 pb-20"
              dataLength={state.conversationSets.length}
              next={state.onNextPage}
              hasMore={state.hasMorePages}
              scrollableTarget={id}
              loader={<LeaderboardItemLoader />}>
              {state.conversationSets.map((item) => {
                const sourceInfo = getSourceInfo(item?.conversation);
                return (
                  <AdaptiveFlexLeftPaneItem
                    key={item.conversation.id}
                    itemId={item.conversation.id}
                    onClick={() => onSelectConversation(item)}>
                    <div className="flex w-full items-center justify-between overflow-hidden">
                      <div className="pr-3 md:pr-6 flex-1">
                        <span className="text-md line-clamp-1 truncate whitespace-pre-wrap font-medium">
                          {(() => {
                            const conv = item?.conversation;
                            if (conv?.miscellaneous?.from === 'direct_message') {
                              const participants: any[] =
                                (item as any)?.direct_message_participants || [];
                              const meId = state?.creator_id;
                              const other =
                                participants.find((p) => p?.reference_id !== meId) ||
                                participants[0];
                              return (
                                other?.user_name ||
                                other?.display_name ||
                                conv?.miscellaneous?.direct_message_details?.created_by_name ||
                                conv?.name
                              );
                            }
                            return getConversationTitle(conv);
                          })()}
                        </span>
                        {/* {sourceInfo && (
                          <div className="flex items-center text-[11px] space-x-1 text-note opacity-75">
                            <span className="">From:</span>
                       
                            <span className="truncate">
                              {sourceInfo.type}
                              {sourceInfo.name && (
                                <span className="ml-1 font-bold text-ellipsis max-w-[120px] overflow-hidden text-nowrap">
                                  {sourceInfo.name}
                                </span>
                              )}
                            </span>
                          </div>
                        )} */}

                        {(() => {
                          const preview = getConversationPreviewText(
                            item,
                            messageState.user_id,
                            state?.user_username,
                          );
                          if (!preview.text) return null;

                          const cleanedText = preview.text
                            ?.replace(/<[^>]*>/g, '')
                            ?.replace(/&nbsp;/g, ' ')
                            ?.replace(/</g, '')
                            ?.replace(/\n/g, ' ');
                          const maxLength = 30;
                          const displayText =
                            cleanedText && cleanedText.length > maxLength
                              ? cleanedText.slice(0, maxLength) + '...'
                              : cleanedText;

                          return (
                            <div
                              style={{ lineHeight: 'normal' }}
                              className="text-note flex flex-row items-center gap-1">
                              {preview.showSender && item.user_name_last_message && (
                                <span className="text-note text-xs">
                                  {item?.user_name_last_message === state?.user_username
                                    ? 'You'
                                    : item?.user_name_last_message}
                                  :
                                </span>
                              )}
                              <span className="text-note line-clamp-1 truncate whitespace-pre-wrap text-xs">
                                {displayText}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center justify-end gap-2 min-w-fit ml-3 md:ml-6">
                        <Text type="xs" className="text-note whitespace-nowrap">
                          {formatConversationPreviewTime(
                            item?.last_message || item?.conversation?.updated_at,
                          )}
                        </Text>

                        {(() => {
                          const raw = Number(item?.unread_messages_count) || 0;
                          const count = Math.min(raw, 99);
                          if (count <= 0) return null;
                          return (
                            <div className="mx-2 h-min rounded-full bg-primary px-2 py-0.5">
                              <Text type="xs" className="text-white break-normal font-bold">
                                {count === 99 && raw > 99 ? '99+' : count}
                              </Text>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </AdaptiveFlexLeftPaneItem>
                );
              })}
            </InfiniteScroll>
          </div>
        </AdaptiveFlexLeftPane>

        <AdaptiveFlexRightPane className="h-full overflow-hidden p-0 md:w-3/4">
          {state.selectedConversationSet && (
            <ConversationMessagesPane
              conversationSet={state.selectedConversationSet}
              onReadNewMessage={onReadNewMessage}
              singleConversationSet={state.singleConversationSet}
            />
          )}
        </AdaptiveFlexRightPane>
      </AdaptiveFlexPanel>

      {state.settingsModalState && state.selectedConversationSet && (
        <ConversationSettings
          restrictions_list={
            state.selectedConversationSet?.restrictions_list || {
              fans_react: false,
              fans_reply: false,
              fans_report: false,
            }
          }
          conversationSet={state.selectedConversationSet}
        />
      )}
    </>
  );
};
